import React from "react";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import executiveValueFormatter from "src/scenes/overview/overview-components/executive-value-formatter";
import styled from "styled-components";
import { Box, Paper, Stack } from "@mui/material";
import ReactCardFlip from "react-card-flip";
import calculateDependencies from "src/scenes/overview/overview-components/calculate-dependencies";
import AccuracyLinear from "src/scenes/overview/overview-components/accuracy-linear";
import { docsPathConstants, getDocsPath } from "src/utils/docs-path-constants";
import { useOrg } from "src/components/providers/org-provider";
import ExternalLink from "../../../../components/external-link";

const CustomPaper = styled(Paper)`
    width: 100%;
    min-height: 200px;
    padding: 16px;
    border-radius: 14px;
`;

const MetricHeaderText = styled.p`
    margin: 12px 0 12px 0;
    font-size: 15px;
    font-weight: 600;
`;

const MetricBreakdownText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 300;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 200;
`;

const BreakdownContainer = styled(Box)`
    padding: 12px;
    border-radius: 12px;
    background: #393939;
    margin-top: 12px;
`;

const InvestmentSection = ({ data, fetchAction, expandedView, isFlipped }) => {
    const { org } = useOrg();
    return (
        <ReactCardFlip isFlipped={isFlipped} containerStyle={{ width: "100%", height: "100%" }}>
            <CustomPaper>
                {!isFlipped && (
                    <>
                        <Stack direction="column" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Code Days</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.code &&
                                        data.code.meta &&
                                        data.code.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction && fetchAction.data && fetchAction.data.code
                                }
                            >
                                {executiveValueFormatter(
                                    data && data.code && data.code.total.value,
                                    "time",
                                    false,
                                    "per user-work day",
                                    data && data.code && data.code.stats && data.code.stats.slope
                                )}
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>Total sum:</MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.code &&
                                                fetchAction.data.code.breakdown.total
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.code &&
                                                    data.code.breakdown &&
                                                    data.code.breakdown.total,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Per day (summed users):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.code &&
                                                fetchAction.data.code.breakdown.avgPerDay
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.code &&
                                                    data.code.breakdown &&
                                                    data.code.breakdown.avgPerDay,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Per user (for entire period):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.code &&
                                                fetchAction.data.code.breakdown.avgPerUser
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.code &&
                                                    data.code.breakdown &&
                                                    data.code.breakdown.avgPerUser,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                        <Stack direction="column" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Collaboration Days</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.collaboration &&
                                        data.collaboration.meta &&
                                        data.collaboration.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction &&
                                    fetchAction.data &&
                                    fetchAction.data.collaboration
                                }
                            >
                                {executiveValueFormatter(
                                    data && data.collaboration && data.collaboration.total.value,
                                    "time",
                                    false,
                                    "per user-work day",
                                    data &&
                                        data.collaboration &&
                                        data.collaboration.stats &&
                                        data.collaboration.stats.slope
                                )}
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>Total sum:</MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.collaboration &&
                                                fetchAction.data.collaboration.breakdown.total
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.collaboration &&
                                                    data.collaboration.breakdown.total,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Per day (summed users):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.collaboration &&
                                                fetchAction.data.collaboration.breakdown.avgPerDay
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.collaboration &&
                                                    data.collaboration.breakdown.avgPerDay,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Per user (for entire period):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.collaboration &&
                                                fetchAction.data.collaboration.breakdown.avgPerUser
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.collaboration &&
                                                    data.collaboration.breakdown.avgPerUser,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                        <Stack direction="column" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Balance Days</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.balance &&
                                        data.balance.meta &&
                                        data.balance.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction && fetchAction.data && fetchAction.data.balance
                                }
                            >
                                {executiveValueFormatter(
                                    data && data.balance && data.balance.total.value,
                                    "time",
                                    false,
                                    "per user-work day",
                                    data &&
                                        data.balance &&
                                        data.balance.stats &&
                                        data.balance.stats.slope
                                )}
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>Total sum:</MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.balance &&
                                                fetchAction.data.balance.breakdown &&
                                                fetchAction.data.balance.breakdown.outOfOfficeBreak
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.balance &&
                                                    data.balance.breakdown &&
                                                    data.balance.breakdown.outOfOfficeBreak,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Total Out of Office:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.balance &&
                                                fetchAction.data.balance.breakdown &&
                                                fetchAction.data.balance.breakdown.outOfOffice
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.balance &&
                                                    data.balance.breakdown &&
                                                    data.balance.breakdown.outOfOffice,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Out of Office (per user-work day):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.balance &&
                                                fetchAction.data.balance.breakdown &&
                                                fetchAction.data.balance.breakdown
                                                    .avgPerUserDayOutOfOffice
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.balance &&
                                                    data.balance.breakdown &&
                                                    data.balance.breakdown.avgPerUserDayOutOfOffice,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>Total Breaks:</MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.breakdown &&
                                                fetchAction.data.balance.breakdown &&
                                                fetchAction.data.balance.breakdown.break
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.balance &&
                                                    data.balance.breakdown &&
                                                    data.balance.breakdown.break,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Breaks (per user-work day):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.balance &&
                                                fetchAction.data.balance.breakdown &&
                                                fetchAction.data.balance.breakdown
                                                    .avgPerUserDayBreak
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.balance &&
                                                    data.balance.breakdown &&
                                                    data.balance.breakdown.avgPerUserDayBreak,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                    </>
                )}
            </CustomPaper>
            <CustomPaper>
                {isFlipped && (
                    <>
                        <MetricHeaderText
                            style={{ margin: "0 0 10px 0", fontWeight: 200, opacity: 0.5 }}
                        >
                            Investment
                        </MetricHeaderText>
                        <InfoText>
                            Time invested by the company to this group of people, indication of the
                            time provided to do their core activities.
                        </InfoText>
                        <Stack direction="column" mb={2} spacing={1}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(org, docsPathConstants.codeDaysDefinition)}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Code Days
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Person-working days that were made available for them to work on
                                code (time without breaks, out of office and collaboration).
                            </InfoText>
                            <InfoText>Data source impact:</InfoText>
                            {calculateDependencies(
                                data && data.code && data.code.meta.accuracy.dependencies
                            )}
                        </Stack>
                        <Stack direction="column" mb={2} spacing={1}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(
                                        org,
                                        docsPathConstants.collaborationDaysDefinition
                                    )}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Collaboration Days
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Person-working days invested in collaboration (ie chatting and in
                                meetings).
                            </InfoText>
                            <InfoText>Data source impact:</InfoText>
                            {calculateDependencies(
                                data &&
                                    data.collaboration &&
                                    data.collaboration.meta.accuracy.dependencies
                            )}
                        </Stack>
                        <Stack direction="column" mb={2} spacing={1}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(org, docsPathConstants.balanceDaysDefinition)}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Balance Days
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Person-days invested in the team&apos;s healthy work-life balance
                                (ie breaks and out of office)
                            </InfoText>
                            <InfoText>Data source impact:</InfoText>
                            {calculateDependencies(
                                data && data.balance && data.balance.meta.accuracy.dependencies
                            )}
                        </Stack>
                    </>
                )}
            </CustomPaper>
        </ReactCardFlip>
    );
};

export default InvestmentSection;
